import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import ChainedBackend from 'i18next-chained-backend'
import LanguageDetector from 'i18next-browser-languagedetector'
import LocalStorageBackend from 'i18next-localstorage-backend'
import FirebaseBackend, { TRANSLATION_CACHE_EXPIRATION_MS } from './i18n/backends/FirebaseBackend'

/**
 * Preload local translations for the detected language
 * This ensures translations are available before React renders
 */
async function preloadLocalTranslations(language: string, namespaces: string[]): Promise<void> {
  const loadPromises = namespaces.map(async (namespace) => {
    try {
      const loadPath = `/locales/${language}/${namespace}.json`
      const response = await fetch(loadPath, { cache: 'no-cache' })

      if (response.ok) {
        const data = await response.json()
        if (data && typeof data === 'object') {
          // Fills gaps only: this races the backend load, and overwriting would rewrite delivered
          // copy mid-render — the invite screen swapped its heading 33 ms after first paint.
          i18n.addResourceBundle(language, namespace, data, true, false)
        }
      }
    } catch (error) {
      // Silently fail - backend will handle fallback
    }
  })

  await Promise.all(loadPromises)
}

// Detect language early (before init)
// Safely access localStorage (may not be available in test mode)
const getLocalStorageItem = (key: string): string | null => {
  try {
    if (typeof window !== 'undefined' && window.localStorage && typeof window.localStorage.getItem === 'function') {
      return window.localStorage.getItem(key)
    }
  } catch {
    // localStorage may not be available (e.g., in test mode)
  }
  return null
}

const detectedLanguage =
  getLocalStorageItem('i18nextLng') || (typeof document !== 'undefined' && document.documentElement.lang) || 'en'

const namespaces = ['translation', 'feedbackTexts']

// Skip preloading in test mode - tests use mocked i18n
const isTestMode = import.meta.env.MODE === 'test' || process.env.NODE_ENV === 'test'

// Start preloading local translations in background (don't wait for it)
// FirebaseBackend will load local translations instantly when needed anyway
if (!isTestMode) {
  preloadLocalTranslations(detectedLanguage, namespaces).catch(() => {
    // Silently fail - backend handles fallback
  })
}

i18n
  // Chained Backend: LocalStorage (cache) first, Firebase (source) fallback
  .use(ChainedBackend)
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)

// Initialize i18next
i18n.init({
  backend: {
    backends: [LocalStorageBackend, FirebaseBackend],
    backendOptions: [
      {
        prefix: 'i18next_res_',
        expirationTime: TRANSLATION_CACHE_EXPIRATION_MS,
        defaultVersion: 'v1.2.29', // bump when deploying translation changes to force cache invalidation
        store: typeof window !== 'undefined' ? window.localStorage : null
      },
      {} // FirebaseBackend has no options
    ]
  },

  // Available languages
  supportedLngs: ['en', 'sv'],

  // Language detection options
  detection: {
    // Order of detection methods - localStorage first, then fallback to English
    order: ['localStorage', 'htmlTag'],

    // Cache the language preference
    caches: ['localStorage'],

    // Default to English if no language is detected
    defaultLanguage: 'en'
  },

  // Don't set lng explicitly - let the language detector handle it
  // lng: 'en', // REMOVED: This was overriding localStorage detection

  // Backend options are handled by the custom backends
  // FirebaseBackend loads local first, returns immediately, then loads Firebase in background

  // React-i18next options
  react: {
    // Disable Suspense - we show local translations immediately, then update with BE translations
    useSuspense: false,
    // Bind i18n instance to react
    bindI18n: 'languageChanged loaded',
    // Bind i18nStore to react
    bindI18nStore: 'added removed',
    // TransSuspense
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p']
  },

  // Interpolation options
  interpolation: {
    // React already does escaping
    escapeValue: false
  },

  // Fallback options
  fallbackLng: {
    default: ['en']
  },

  // Debug mode for development (set to false to reduce console noise)
  // Only show critical errors, not every missing key
  debug: false,

  // Default namespace
  defaultNS: 'translation',

  // Available namespaces
  ns: namespaces,

  // Key separator (use dots for nested keys)
  keySeparator: '.',

  // Namespace separator
  nsSeparator: ':',

  // Save missing translations - DISABLED to prevent render-phase state updates
  saveMissing: false,

  // Load all namespaces at once
  load: 'currentOnly',

  // Preload languages
  preload: ['en', 'sv']
})

// Initialize translation monitoring (replaces old missing key logging)
// Skip in test mode - tests use mocked i18n
if (!isTestMode) {
  import('./i18n/monitoring/TranslationMonitor')
  import('./i18n/monitoring/SlackAlerter')
}

// Export i18n instance
export default i18n

// Type definitions for better TypeScript support
declare module 'react-i18next' {
  interface CustomTypeOptions {
    returnNull: false
  }
}
